// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — fiche d'un dispositif et son cycle de vie :
//   Brouillon → Simulé → À valider → Actif ⇄ Suspendu → Archivé (reprise du brouillon, refus motivé).
// Boutons selon l'état et les pouvoirs de mes mandats dans l'organisation (préparer, valider, suspendre) ;
// le serveur reste juge : la personne qui a préparé, simulé ou soumis ne valide jamais (refus « double regard »).
// Historique des actes (acteur, fonction, « par intérim »), capacités, pièces ; types d'aide d'un dispositif AIDE (TypesAide.jsx).
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Archive, CheckCircle2, CirclePause, CirclePlay, FlaskConical, Plus, Send, Undo2, XCircle, X,
} from 'lucide-react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { EnTetePage, Badge, Bouton, BandeauInfo, Squelette, formaterDate } from '../../design/composants';
import {
  useDs, toArray, libelle, dateHeure, TYPES_DISPOSITIF, ETATS_DISPOSITIF, CYCLE_DISPOSITIF, CAPACITES, ACTIONS_EVENEMENT,
} from './api';
import { useEspaces, droitsDans } from './espaces';
import { useLecture, ErreurServeur, AccesRefuseDs, Etat, FenetreConfirmer, FenetreMotif, ListePieces } from './commun';
import ReglementCarte from './ReglementCarte';
import TypesAide from './TypesAide';

/** Transitions offertes par état (contrat du socle : README § 3). */
const TRANSITIONS = {
  BROUILLON: [{ vers: 'SIMULE', cle: 'ds_dispositif_preparer', libelle: 'Simuler', Icone: FlaskConical, principal: true, fait: 'Dispositif simulé.',
    texte: 'Enregistre l\'étape de simulation. Le contrôle budgétaire du règlement est fait par le Centre de configuration.' }],
  SIMULE: [
    { vers: 'A_VALIDER', cle: 'ds_dispositif_preparer', libelle: 'Soumettre à validation', Icone: Send, principal: true, fait: 'Dispositif soumis à validation.',
      texte: 'Le dispositif sera validé par une personne qui ne l\'a ni préparé, ni simulé, ni soumis.' },
    { vers: 'BROUILLON', cle: 'ds_dispositif_preparer', libelle: 'Reprendre le brouillon', Icone: Undo2, fait: 'Dispositif revenu en brouillon.', texte: 'Le dispositif revient en brouillon pour être corrigé.' },
  ],
  A_VALIDER: [
    { vers: 'ACTIF', cle: 'ds_dispositif_valider', libelle: 'Valider et activer', Icone: CheckCircle2, principal: true, fait: 'Dispositif validé : il est actif.',
      texte: 'Le dispositif devient actif. Vous ne pouvez pas valider un dispositif que vous avez préparé, simulé ou soumis.' },
    { vers: 'BROUILLON', cle: 'ds_dispositif_valider', libelle: 'Refuser', Icone: XCircle, motif: true, danger: true, fait: 'Dispositif refusé : il revient en brouillon.',
      aide: 'Le dispositif revient en brouillon ; le motif est conservé dans l\'historique.' },
  ],
  ACTIF: [
    { vers: 'SUSPENDU', cle: 'ds_dispositif_suspendre', libelle: 'Suspendre', Icone: CirclePause, motif: true, fait: 'Dispositif suspendu.', aide: 'Plus aucun nouveau dossier n\'est accepté pendant la suspension.' },
    { vers: 'ARCHIVE', cle: 'ds_dispositif_suspendre', libelle: 'Archiver', Icone: Archive, motif: true, danger: true, fait: 'Dispositif archivé.', aide: 'L\'archivage est définitif.' },
  ],
  SUSPENDU: [
    { vers: 'ACTIF', cle: 'ds_dispositif_suspendre', libelle: 'Reprendre', Icone: CirclePlay, motif: true, principal: true, fait: 'Dispositif repris : il est actif.', aide: 'Le dispositif redevient actif.' },
    { vers: 'ARCHIVE', cle: 'ds_dispositif_suspendre', libelle: 'Archiver', Icone: Archive, motif: true, danger: true, fait: 'Dispositif archivé.', aide: 'L\'archivage est définitif.' },
  ],
};
const QUI_AGIT = {
  BROUILLON: 'la préparation (Secrétaire général)', SIMULE: 'la préparation (Secrétaire général)',
  A_VALIDER: 'la validation (Président), par une autre personne que celle qui a préparé', ACTIF: 'la suspension ou l\'archivage (Président)',
  SUSPENDU: 'la suspension ou l\'archivage (Président)',
};
const ETAPES_PREPARATION = ['PREPARER', 'SIMULER', 'SOUMETTRE', 'REPRENDRE'];

/** Frise du cycle de vie : étapes franchies, étape courante. */
function Cycle({ etat }) {
  const rang = CYCLE_DISPOSITIF.indexOf(etat);
  return (
    <ol aria-label="Cycle de vie du dispositif" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 0', listStyle: 'none', margin: '0 0 20px', padding: 0 }}>
      {CYCLE_DISPOSITIF.map((c, i) => {
        const courant = c === etat;
        const franchi = i < rang && !(etat === 'ARCHIVE' && c === 'SUSPENDU');
        const couleur = courant ? 'var(--sg-primaire)' : franchi ? 'var(--sg-texte-2)' : 'var(--sg-texte-4)';
        return (
          <li key={c} aria-current={courant ? 'step' : undefined} style={{ display: 'inline-flex', alignItems: 'center', fontSize: 13, color: couleur, fontWeight: courant ? 600 : 400 }}>
            {i > 0 && <span aria-hidden="true" style={{ width: 24, height: 1, background: 'var(--sg-bord-fort)', margin: '0 8px' }} />}
            <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: '50%', marginRight: 6, background: courant ? 'var(--sg-primaire)' : franchi ? 'var(--sg-texte-3)' : 'transparent', border: `1px solid ${courant ? 'var(--sg-primaire)' : 'var(--sg-bord-fort)'}` }} />
            {libelle(ETATS_DISPOSITIF, c)}
          </li>
        );
      })}
    </ol>
  );
}

function Historique({ lignes }) {
  if (!lignes.length) return <p className="rh-cellule-secondaire" style={{ margin: 0 }}>Aucun acte enregistré.</p>;
  return (
    <div className="rh-conteneur-tableau">
      <table className="rh-tableau">
        <thead><tr><th>Date</th><th>Acte</th><th>État</th><th>Par</th><th>Motif</th></tr></thead>
        <tbody>
          {lignes.map((h) => (
            <tr key={h.id}>
              <td style={{ whiteSpace: 'nowrap' }}>{dateHeure(h.le)}</td>
              <td>{libelle(ACTIONS_EVENEMENT, h.action)}</td>
              <td>{h.de_etat ? `${libelle(ETATS_DISPOSITIF, h.de_etat)} → ${libelle(ETATS_DISPOSITIF, h.vers_etat)}` : libelle(ETATS_DISPOSITIF, h.vers_etat)}</td>
              <td>
                {h.acteur || <span className="rh-cellule-secondaire">Système</span>}
                {h.fonction && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{h.fonction}{h.par_interim ? ', par intérim' : h.nature_mandat === 'DELEGATION' ? ', par délégation' : ''}</span>}
              </td>
              <td>{h.motif || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Capacites({ d, peutModifier, onAjouter, onRetirer }) {
  const [choix, setChoix] = useState('');
  const actives = toArray(d.capacites);
  const possibles = Object.keys(CAPACITES).filter((c) => !actives.includes(c));
  return (
    <div>
      {!actives.length && <p className="rh-cellule-secondaire" style={{ margin: '0 0 8px' }}>Aucune capacité active.</p>}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {actives.map((c) => (
          <Badge key={c} ton="neutre">
            {libelle(CAPACITES, c)}
            {peutModifier && (
              <button type="button" onClick={() => onRetirer(c)} aria-label={`Retirer la capacité ${libelle(CAPACITES, c)}`} title="Retirer"
                style={{ border: 0, background: 'none', padding: 0, marginLeft: 4, cursor: 'pointer', color: 'inherit', display: 'inline-flex' }}>
                <X size={12} aria-hidden="true" />
              </button>
            )}
          </Badge>
        ))}
      </div>
      {peutModifier && possibles.length > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select className="rh-champ-saisie" style={{ width: 'auto' }} value={choix} onChange={(e) => setChoix(e.target.value)} aria-label="Capacité à ajouter">
            <option value="">Ajouter une capacité…</option>
            {possibles.map((c) => <option key={c} value={c}>{libelle(CAPACITES, c)}</option>)}
          </select>
          <Bouton petit Icone={Plus} disabled={!choix} onClick={() => { onAjouter(choix); setChoix(''); }}>Ajouter</Bouton>
        </div>
      )}
    </div>
  );
}

export default function DispositifPage() {
  const { org, id } = useParams();
  const ds = useDs();
  const { employe } = useAuth();
  const { espaces } = useEspaces();
  const detail = useLecture(() => ds.dispositif(id), [ds, id]);
  const organisation = useLecture(() => ds.organisation(org), [ds, org]);
  const [fenetre, setFenetre] = useState(null); // { type: 'transition' | 'retirer', ... }
  const [info, setInfo] = useState('');
  const [erreurCapacite, setErreurCapacite] = useState(null);
  const d = detail.donnees;
  const droits = droitsDans(espaces, d?.organisation_id || org);
  const historique = toArray(d?.historique);
  const transitions = d ? toArray(TRANSITIONS[d.etat]) : [];
  const permises = transitions.filter((t) => droits.a(t.cle));

  // Indice (le serveur tranche) : la personne connectée figure-t-elle parmi les préparateurs ?
  const moi = [employe?.prenom, (employe?.nom || '').toUpperCase()].filter(Boolean).join(' ').trim();
  const aPrepare = !!moi && historique.some((h) => ETAPES_PREPARATION.includes(h.etape) && h.acteur === moi);

  const fil = [
    { libelle: 'Dispositifs sociaux', vers: '/dispositifs' },
    { libelle: organisation.donnees?.libelle || 'Organisation', vers: `/dispositifs/${org}/dispositifs` },
    { libelle: d ? d.libelle : 'Dispositif' },
  ];
  const apres = (message) => { setFenetre(null); setInfo(message); detail.recharger(); };
  const transiter = async (t, motif) => {
    await ds.transition(d.id, t.vers, motif);
    apres(t.fait);
  };
  const ajouterCapacite = async (c) => {
    setErreurCapacite(null);
    try { await ds.ajouterCapacite(d.id, c); detail.recharger(); } catch (e) { setErreurCapacite(e); }
  };

  const pieces = historique.filter((h) => h.piece_id).map((h) => ({ id: h.piece_id, contexte: libelle(ACTIONS_EVENEMENT, h.action) }));

  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={fil} titre={d ? d.libelle : 'Dispositif'}
          actions={permises.length ? permises.map((t) => (
            <Bouton key={`${t.vers}-${t.libelle}`} variante={t.danger ? 'danger' : t.principal ? 'primaire' : 'secondaire'} Icone={t.Icone}
              onClick={() => { setInfo(''); setFenetre({ type: 'transition', t }); }}>{t.libelle}</Bouton>
          )) : null} />
        {detail.erreur && (detail.erreur.statut === 403 || detail.erreur.statut === 404
          ? <section className="rh-carte"><AccesRefuseDs erreur={detail.erreur} /></section>
          : <ErreurServeur erreur={detail.erreur} />)}
        {!d && !detail.erreur && <Squelette hauteur={160} />}
        {d && (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '-8px 0 16px', fontSize: 14, color: 'var(--sg-texte-2)' }}>
              <Etat table={ETATS_DISPOSITIF} code={d.etat} />
              <span>{libelle(TYPES_DISPOSITIF, d.type)}</span><span aria-hidden="true">·</span><span>{d.code}</span>
              {d.valide_le && <><span aria-hidden="true">·</span><span>Validé le {formaterDate(d.valide_le)}</span></>}
              {d.donnees_demo && <><span aria-hidden="true">·</span><span>Données de démonstration</span></>}
            </div>
            {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
            {d.etat === 'A_VALIDER' && aPrepare && droits.a('ds_dispositif_valider') && (
              <BandeauInfo ton="alerte" titre="Validation par une autre personne">
                Vous avez participé à la préparation de ce dispositif : sa validation revient à une autre personne habilitée (règle du double regard).
              </BandeauInfo>
            )}
            <section className="rh-carte">
              <Cycle etat={d.etat} />
              {d.etat === 'ARCHIVE' && <p style={{ margin: 0, fontSize: 14 }}>Dispositif archivé : il ne change plus d'état.</p>}
              {d.etat !== 'ARCHIVE' && !permises.length && (
                <p style={{ margin: 0, fontSize: 14, color: 'var(--sg-texte-2)' }}>
                  Aucune action ne vous revient à cette étape : elle relève de {QUI_AGIT[d.etat]}.
                </p>
              )}
              {d.etat === 'BROUILLON' && droits.a('ds_dispositif_preparer') && (
                <p className="rh-form-aide" style={{ margin: '8px 0 0' }}>Le règlement du dispositif (montants, plafonds, dates) se prépare dans le Centre de configuration avant la simulation.</p>
              )}
            </section>

            <section className="rh-carte">
              <h2 className="rh-form-section-titre" style={{ marginTop: 0 }}>Présentation au salarié</h2>
              {d.texte_salarie ? <p style={{ margin: 0, fontSize: 14, whiteSpace: 'pre-wrap' }}>{d.texte_salarie}</p> : <p className="rh-cellule-secondaire" style={{ margin: 0 }}>Non renseignée.</p>}
              <h2 className="rh-form-section-titre" style={{ marginTop: 24 }}>Capacités</h2>
              <ErreurServeur erreur={erreurCapacite} />
              <Capacites d={d} peutModifier={d.etat === 'BROUILLON' && droits.a('ds_dispositif_preparer')}
                onAjouter={ajouterCapacite} onRetirer={(c) => setFenetre({ type: 'retirer', capacite: c })} />
            </section>

            <ReglementCarte dispositif={d} peutPreparer={droits.a('ds_dispositif_preparer')} />
            {d.type === 'AIDE' && <TypesAide dispositif={d} droits={droits} />}
            <section className="rh-carte">
              <h2 className="rh-form-section-titre" style={{ marginTop: 0 }}>Historique</h2>
              <Historique lignes={historique} />
            </section>
            <section className="rh-carte">
              <h2 className="rh-form-section-titre" style={{ marginTop: 0 }}>Pièces</h2>
              <ListePieces pieces={pieces} vide="Aucune pièce n'est rattachée aux actes de ce dispositif." />
            </section>
          </>
        )}

        {fenetre?.type === 'transition' && !fenetre.t.motif && (
          <FenetreConfirmer titre={fenetre.t.libelle} Icone={fenetre.t.Icone} libelleBouton={fenetre.t.libelle} texte={fenetre.t.texte}
            onFermer={() => setFenetre(null)} onConfirmer={() => transiter(fenetre.t)} />
        )}
        {fenetre?.type === 'transition' && fenetre.t.motif && (
          <FenetreMotif titre={fenetre.t.libelle} Icone={fenetre.t.Icone} libelleBouton={fenetre.t.libelle} aide={fenetre.t.aide} danger={!!fenetre.t.danger}
            onFermer={() => setFenetre(null)} onConfirmer={(motif) => transiter(fenetre.t, motif)} />
        )}
        {fenetre?.type === 'retirer' && (
          <FenetreMotif titre={`Retirer la capacité « ${libelle(CAPACITES, fenetre.capacite)} »`} Icone={X} libelleBouton="Retirer"
            onFermer={() => setFenetre(null)} onConfirmer={async (motif) => { await ds.retirerCapacite(d.id, fenetre.capacite, motif); apres('Capacité retirée.'); }} />
        )}
      </div>
    </Layout>
  );
}
